import React, { createContext, useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { meApi, SessionValidationError } from '../api/auth';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (partial: Partial<User>) => void;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null, token: null, login: () => {}, logout: () => {}, updateUser: () => {}, loading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('intrlock_token');
    const savedUser = localStorage.getItem('intrlock_user');
    if (!savedToken || !savedUser) {
      setLoading(false);
      return;
    }
    let cachedUser: User;
    try {
      cachedUser = JSON.parse(savedUser);
    } catch {
      localStorage.removeItem('intrlock_token');
      localStorage.removeItem('intrlock_user');
      setLoading(false);
      return;
    }
    let active = true;
    meApi(savedToken).then((verified) => {
      if (!active || localStorage.getItem('intrlock_token') !== savedToken) return;
      const current = {
        ...cachedUser,
        role: verified.role || cachedUser.role,
        integrator_id: verified.dealer_id ?? cachedUser.integrator_id,
        dealer_id: verified.dealer_id ?? cachedUser.dealer_id,
        customer_id: verified.customer_id ?? cachedUser.customer_id,
        property_id: verified.property_id ?? cachedUser.property_id,
      };
      setToken(savedToken);
      setUser(current);
      localStorage.setItem('intrlock_user', JSON.stringify(current));
    }).catch((error) => {
      if (!active || localStorage.getItem('intrlock_token') !== savedToken) return;
      if (error instanceof SessionValidationError && (error.status === 401 || error.status === 403)) {
        localStorage.removeItem('intrlock_token');
        localStorage.removeItem('intrlock_user');
        setToken(null);
        setUser(null);
      } else {
        // A transient auth outage is not proof that a valid session expired.
        setToken(savedToken);
        setUser(cachedUser);
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const login = useCallback((newToken: string, newUser: User) => {
    localStorage.setItem('intrlock_token', newToken);
    localStorage.setItem('intrlock_user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('intrlock_token');
    localStorage.removeItem('intrlock_user');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...partial };
      localStorage.setItem('intrlock_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
